import React, { useState, useEffect, useRef } from 'react';
import MessageList from './MessageList';
import MessageInput from './MessageInput';
import { transformMessage } from '../services/aiService';
import { listenToMessages, sendMessage, getUserByHandle, updateUserStatus, getAllUsers, setUserOnline, setUserOffline, updateUserStatusOnly, updateUserName } from '../services/chatService';

const ChatRoom = ({ user, targetHandle, setTargetHandle, onLogout, onClose, isMobile = false, onNewMessage }) => {
    const [messages, setMessages] = useState([]);
    const [isTyping, setIsTyping] = useState(false);
    const [isTransforming, setIsTransforming] = useState(false);
    const [targetUser, setTargetUser] = useState(null);
    const messagesEndRef = useRef(null);
    const [position, setPosition] = useState({ x: 150, y: 50 });
    const [isDragging, setIsDragging] = useState(false);
    const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
    const [chatHistory, setChatHistory] = useState([]);
    const [showSidebar, setShowSidebar] = useState(false);
    const [selectedVibe, setSelectedVibe] = useState('happy');
    const [showVibeSelector, setShowVibeSelector] = useState(false);
    const [isTargetOnline, setIsTargetOnline] = useState(false);
    const [connectionStatus, setConnectionStatus] = useState('disconnected');
    const [showUserList, setShowUserList] = useState(true); // Show user list by default
    const [allUsers, setAllUsers] = useState([]);
    const [showProfileModal, setShowProfileModal] = useState(false);
    const [userStatus, setUserStatus] = useState('online');
    const [userProfilePicture, setUserProfilePicture] = useState('default');
    const [userListPosition, setUserListPosition] = useState({ x: position.x + 600, y: position.y });
    const [isEditingName, setIsEditingName] = useState(false);
    const [editingName, setEditingName] = useState(user.name);
    const [currentUser, setCurrentUser] = useState(user);
    const [isDraggingUserList, setIsDraggingUserList] = useState(false);
    const [userListDragOffset, setUserListDragOffset] = useState({ x: 0, y: 0 });
    const [usersWithUnreadMessages, setUsersWithUnreadMessages] = useState(new Set());
    const [blinkingUsers, setBlinkingUsers] = useState(new Set());
    const [sessionTimeout, setSessionTimeout] = useState(null);
    const windowRef = useRef(null);
    const userListRef = useRef(null);

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    };

    // Check if target user is online
    const checkTargetUserStatus = async () => {
        if (!targetHandle) {
            setConnectionStatus('disconnected');
            setIsTargetOnline(false);
            return;
        }

        try {
            // Check if target user exists in Firebase
            const targetUser = await getUserByHandle(targetHandle);

            if (targetUser) {
                // Check if user is currently online
                if (targetUser.isOnline) {
                    setConnectionStatus('connected');
                    setIsTargetOnline(true);
                } else {
                    // Check if user was recently active (within last 5 minutes)
                    const lastSeen = targetUser.lastSeen;
                    if (lastSeen) {
                        const lastSeenTime = lastSeen.toDate ? lastSeen.toDate() : new Date(lastSeen);
                        const now = new Date();
                        const fiveMinutes = 5 * 60 * 1000;

                        if ((now - lastSeenTime) < fiveMinutes) {
                            setConnectionStatus('recently_active');
                            setIsTargetOnline(true);
                        } else {
                            setConnectionStatus('offline');
                            setIsTargetOnline(false);
                        }
                    } else {
                        setConnectionStatus('offline');
                        setIsTargetOnline(false);
                    }
                }
            } else {
                setConnectionStatus('user_not_found');
                setIsTargetOnline(false);
            }
        } catch (error) {
            console.error('Error checking user status:', error);
            setConnectionStatus('error');
            setIsTargetOnline(false);
        }
    };

    // Load chat history from localStorage
    useEffect(() => {
        const savedHistory = localStorage.getItem(`goodVibes_chatHistory_${user.handle}`);
        if (savedHistory) {
            try {
                const history = JSON.parse(savedHistory);
                setChatHistory(history);
            } catch (error) {
                console.error('Error loading chat history:', error);
            }
        }
    }, [user.handle]);

    // Check target user status when targetHandle changes
    useEffect(() => {
        checkTargetUserStatus();

        // Set up periodic checking every 30 seconds
        const interval = setInterval(checkTargetUserStatus, 30000);

        return () => clearInterval(interval);
    }, [targetHandle]);

    // Load all users when user list is shown
    useEffect(() => {
        const loadAllUsers = async () => {
            if (showUserList) {
                try {
                    const users = await getAllUsers();
                    setAllUsers(users);
                } catch (error) {
                    console.error('Error loading users:', error);
                }
            }
        };

        loadAllUsers();

        // Set up periodic refresh every 30 seconds when user list is shown
        const interval = showUserList ? setInterval(loadAllUsers, 30000) : null;

        return () => {
            if (interval) clearInterval(interval);
        };
    }, [showUserList]);

    // Refresh user list when user status changes
    const refreshUserList = async () => {
        if (showUserList) {
            try {
                const users = await getAllUsers();
                setAllUsers(users);
            } catch (error) {
                console.error('Error refreshing user list:', error);
            }
        }
    };

    // Handle name editing
    const handleEditName = () => {
        setIsEditingName(true);
        setEditingName(currentUser.name);
    };

    const handleSaveName = async () => {
        if (editingName.trim() && editingName.trim() !== currentUser.name) {
            try {
                await updateUserName(user.id, editingName.trim());
                const updatedUser = { ...currentUser, name: editingName.trim() };
                setCurrentUser(updatedUser);
                // Update localStorage
                localStorage.setItem('currentUser', JSON.stringify(updatedUser));
                setIsEditingName(false);
            } catch (error) {
                console.error('Error updating name:', error);
                alert('Error updating name. Please try again.');
            }
        } else {
            setIsEditingName(false);
        }
    };

    const handleCancelEdit = () => {
        setIsEditingName(false);
        setEditingName(currentUser.name);
    };

    // Update user list position when chat window position changes
    useEffect(() => {
        if (showUserList) {
            setUserListPosition({ x: position.x + 600, y: position.y });
        }
    }, [position, showUserList]);

    // Initialize user list position when first shown
    useEffect(() => {
        if (showUserList && userListPosition.x === position.x + 600) {
            setUserListPosition({ x: position.x + 600, y: position.y });
        }
    }, [showUserList]);

    // Set user online when component mounts and update last active time
    useEffect(() => {
        const setOnline = async () => {
            try {
                await setUserOnline(user.id);
            } catch (error) {
                console.error('Error setting user online:', error);
            }
        };

        setOnline();
        const interval = setInterval(setOnline, 60000); // Update every minute

        // Mark user as offline when component unmounts
        return () => {
            clearInterval(interval);
            setUserOffline(user.id).catch(console.error);
        };
    }, [user.id]);

    // Save chat history to localStorage
    const saveChatHistory = (newHistory) => {
        setChatHistory(newHistory);
        localStorage.setItem(`goodVibes_chatHistory_${user.handle}`, JSON.stringify(newHistory));
    };

    // Add user to chat history
    const addToChatHistory = async (handle) => {
        if (handle === user.handle) return; // Don't add self

        try {
            const userInfo = await getUserByHandle(handle);
            if (userInfo) {
                const newHistory = [
                    { handle: userInfo.handle, name: userInfo.name, lastMessage: '', timestamp: Date.now() },
                    ...chatHistory.filter(chat => chat.handle !== handle)
                ].slice(0, 20); // Keep only last 20 chats

                saveChatHistory(newHistory);
            }
        } catch (error) {
            console.error('Error adding to chat history:', error);
        }
    };

    // Switch to a different chat
    const switchToChat = (handle) => {
        setTargetHandle(handle);
        setShowSidebar(false);
    };

    useEffect(() => {
        scrollToBottom();
    }, [messages, isTyping]);

    // Set up real-time message listening
    useEffect(() => {
        if (targetHandle && user.handle) {
            console.log('Setting up message listener for users:', user.handle, targetHandle);
            const unsubscribe = listenToMessages(user.handle, targetHandle, (newMessages) => {
                console.log('Received messages:', newMessages);
                const previousMessageCount = messages.length;
                setMessages(newMessages);

                // Check for new messages from other users
                if (newMessages.length > previousMessageCount) {
                    const newMessagesFromOthers = newMessages.slice(previousMessageCount);
                    newMessagesFromOthers.forEach(msg => {
                        if (msg.userId !== user.id) {
                            console.log('New message from user:', msg.userId, 'Adding to unread messages');
                            // Add to unread messages
                            setUsersWithUnreadMessages(prev => {
                                const newSet = new Set([...prev, msg.userId]);
                                console.log('Updated unread users:', Array.from(newSet));
                                return newSet;
                            });


                            // Start blinking animation
                            setBlinkingUsers(prev => new Set([...prev, msg.userId]));

                            // Stop blinking after 3 seconds
                            setTimeout(() => {
                                setBlinkingUsers(prev => {
                                    const newSet = new Set(prev);
                                    newSet.delete(msg.userId);
                                    return newSet;
                                });
                            }, 3000);
                        }
                    });
                }

                // Trigger notification if new message arrived
                if (newMessages.length > previousMessageCount && onNewMessage) {
                    onNewMessage();
                }
            });

            return () => {
                console.log('Cleaning up message listener');
                unsubscribe();
            };
        }
    }, [targetHandle, user.handle]);

    // Clear unread messages when switching to a different user and auto-scroll
    useEffect(() => {
        if (targetHandle) {
            // Find the target user ID and clear their unread status
            const targetUser = allUsers.find(u => u.handle === targetHandle);
            if (targetUser) {
                setUsersWithUnreadMessages(prev => {
                    const newSet = new Set(prev);
                    newSet.delete(targetUser.id);
                    return newSet;
                });


                // Auto-scroll to bottom when switching to a user
                setTimeout(() => {
                    scrollToBottom();
                }, 100);
            }
        }
    }, [targetHandle, allUsers]);

    // Update user status when component mounts/unmounts
    useEffect(() => {
        updateUserStatus(user.id, true);

        return () => {
            updateUserStatus(user.id, false);
            // Clear session timeout on unmount
            if (sessionTimeout) {
                clearTimeout(sessionTimeout);
            }
        };
    }, [user.id, sessionTimeout]);

    // Session timeout management - auto logout after 3 minutes
    useEffect(() => {
        const loginTime = localStorage.getItem('loginTime');
        if (loginTime) {
            const now = Date.now();
            const timeDiff = now - parseInt(loginTime);
            const threeMinutes = 3 * 60 * 1000; // 3 minutes in milliseconds
            const remainingTime = threeMinutes - timeDiff;

            if (remainingTime > 0) {
                const timeout = setTimeout(() => {
                    // Session expired, logout
                    onLogout();
                }, remainingTime);

                setSessionTimeout(timeout);

                return () => {
                    clearTimeout(timeout);
                };
            } else {
                // Session already expired
                onLogout();
            }
        }
    }, [onLogout]);

    // Look up target user when handle changes
    useEffect(() => {
        if (targetHandle) {
            getUserByHandle(targetHandle).then(user => {
                setTargetUser(user);
            });
        } else {
            setTargetUser(null);
        }
    }, [targetHandle]);

    const handleSendMessage = async (text) => {
        console.log('Sending message:', text, 'to:', targetHandle);

        if (!targetHandle) {
            alert('Please enter a target user handle first');
            return;
        }

        if (!targetUser) {
            alert('Target user not found. Please check the handle.');
            return;
        }

        setIsTransforming(true);
        setIsTyping(true);

        try {
            console.log('Transforming message with AI...');
            // Transform the message using AI with selected vibe
            const transformedText = await transformMessage(text, selectedVibe);
            console.log('Transformed message:', transformedText);

            // Send message to Firebase
            console.log('Sending to Firebase...');
            await sendMessage(user.id, user.handle, targetHandle, text, transformedText);
            console.log('Message sent successfully');

            // Add target user to chat history
            await addToChatHistory(targetHandle);
        } catch (error) {
            console.error('Error sending message:', error);
            alert('Error sending message. Please try again.');
        } finally {
            setIsTransforming(false);
            setIsTyping(false);
        }
    };

    const handleTargetUserChange = (e) => {
        setTargetHandle(e.target.value);
    };

    const handleTitleBarMouseDown = (e) => {
        if (e.target.classList.contains('window-controls') ||
            e.target.closest('.window-controls')) {
            return;
        }
        setIsDragging(true);
        const rect = windowRef.current.getBoundingClientRect();
        setDragOffset({
            x: e.clientX - rect.left,
            y: e.clientY - rect.top
        });
    };

    const handleMouseMove = (e) => {
        if (isDragging) {
            const newX = e.clientX - dragOffset.x;
            const newY = e.clientY - dragOffset.y;
            setPosition({ x: Math.max(0, newX), y: Math.max(0, newY) });
        }
    };

    const handleMouseUp = () => {
        setIsDragging(false);
    };

    // User List drag handlers
    const handleUserListMouseDown = (e) => {
        if (e.target.classList.contains('close-user-list-btn') ||
            e.target.closest('.close-user-list-btn')) {
            return;
        }
        setIsDraggingUserList(true);
        const rect = userListRef.current.getBoundingClientRect();
        setUserListDragOffset({
            x: e.clientX - rect.left,
            y: e.clientY - rect.top
        });
    };

    const handleUserListMouseMove = (e) => {
        if (isDraggingUserList) {
            const newX = e.clientX - userListDragOffset.x;
            const newY = e.clientY - userListDragOffset.y;
            setUserListPosition({
                x: Math.max(position.x + 50, Math.min(newX, window.innerWidth - 280)),
                y: Math.max(0, newY)
            });
        }
    };

    const handleUserListMouseUp = () => {
        setIsDraggingUserList(false);
    };

    React.useEffect(() => {
        if (isDragging) {
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);
            return () => {
                document.removeEventListener('mousemove', handleMouseMove);
                document.removeEventListener('mouseup', handleMouseUp);
            };
        }
    }, [isDragging, dragOffset, handleMouseMove]);

    React.useEffect(() => {
        if (isDraggingUserList) {
            document.addEventListener('mousemove', handleUserListMouseMove);
            document.addEventListener('mouseup', handleUserListMouseUp);
            return () => {
                document.removeEventListener('mousemove', handleUserListMouseMove);
                document.removeEventListener('mouseup', handleUserListMouseUp);
            };
        }
    }, [isDraggingUserList, userListDragOffset, handleUserListMouseMove]);

    return (
        <div className={`window-overlay ${isMobile ? 'mobile-fullscreen' : ''}`}>
            <div
                className={`win98-window chat-window ${isMobile ? 'mobile-chat-window' : ''}`}
                ref={windowRef}
                style={isMobile ? {
                    position: 'fixed',
                    left: 0,
                    top: 0,
                    width: '100vw',
                    height: '100vh',
                    transform: 'none',
                    transition: 'none'
                } : {
                    position: 'absolute',
                    left: position.x,
                    top: position.y,
                    transform: isDragging ? 'scale(1.02)' : 'scale(1)',
                    transition: isDragging ? 'none' : 'transform 0.1s ease'
                }}
            >
                <div
                    className="window-titlebar"
                    onMouseDown={handleTitleBarMouseDown}
                    style={{ cursor: isDragging ? 'grabbing' : 'grab' }}
                >
                    <div className="window-title">GOOD VIBES* #ccfc - @{user.handle}</div>
                    <div className="window-controls">
                        <button className="minimize-btn">_</button>
                        <button className="maximize-btn">□</button>
                        <button className="close-btn" onClick={onClose}>×</button>
                    </div>
                </div>

                <div className="window-content">
                    <div className="chat-room">
                        <div className="chat-header">
                            <div className="user-info">
                                <div className="user-avatar">👤</div>
                                <div>
                                    {isEditingName ? (
                                        <div className="name-edit-container">
                                            <input
                                                type="text"
                                                value={editingName}
                                                onChange={(e) => setEditingName(e.target.value)}
                                                className="name-edit-input"
                                                autoFocus
                                                onKeyPress={(e) => {
                                                    if (e.key === 'Enter') handleSaveName();
                                                    if (e.key === 'Escape') handleCancelEdit();
                                                }}
                                            />
                                            <div className="name-edit-buttons">
                                                <button
                                                    className="save-name-btn"
                                                    onClick={handleSaveName}
                                                    title="Save"
                                                >
                                                    ✓
                                                </button>
                                                <button
                                                    className="cancel-name-btn"
                                                    onClick={handleCancelEdit}
                                                    title="Cancel"
                                                >
                                                    ✕
                                                </button>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="name-display-container">
                                            <h3>{currentUser.name}</h3>
                                            <button
                                                className="edit-name-btn"
                                                onClick={handleEditName}
                                                title="Edit name"
                                            >
                                                ✏️
                                            </button>
                                        </div>
                                    )}
                                    <p>@{user.handle}</p>
                                </div>
                            </div>

                            <div className="target-user-section">
                                <div className="input-group">
                                    <label>Connect to:</label>
                                    <input
                                        type="text"
                                        value={targetHandle}
                                        onChange={handleTargetUserChange}
                                        placeholder="Enter handle (e.g., coolcat123)"
                                        className="win98-input"
                                    />
                                </div>
                                {targetHandle && (
                                    <div className="target-user-info">
                                        <span className="target-user-name">Connecting to: @{targetHandle}</span>
                                        <span className={`user-status ${isTargetOnline ? 'online' : 'offline'}`}>
                                            {connectionStatus === 'connected' && '● Online'}
                                            {connectionStatus === 'recently_active' && '● Recently Active'}
                                            {connectionStatus === 'offline' && '● Offline'}
                                            {connectionStatus === 'user_not_found' && '● User Not Found'}
                                            {connectionStatus === 'error' && '● Connection Error'}
                                            {connectionStatus === 'disconnected' && '● Not Connected'}
                                        </span>
                                    </div>
                                )}
                            </div>

                            <div className="header-actions">
                                <button
                                    onClick={() => setShowVibeSelector(!showVibeSelector)}
                                    className="win98-button vibe-button"
                                >
                                    {selectedVibe.charAt(0).toUpperCase() + selectedVibe.slice(1)} Vibes
                                </button>
                                <button
                                    onClick={() => setShowSidebar(!showSidebar)}
                                    className="win98-button"
                                >
                                    {showSidebar ? 'Hide' : 'Show'} Chats
                                </button>
                                <button
                                    onClick={() => setShowUserList(!showUserList)}
                                    className="win98-button"
                                >
                                    {showUserList ? 'Hide' : 'Show'} User List
                                </button>
                                <button onClick={onLogout} className="win98-button">
                                    Logout
                                </button>
                            </div>
                        </div>

                        {/* Vibe Selector */}
                        {showVibeSelector && (
                            <div className="vibe-selector">
                                <div className="vibe-selector-header">
                                    <h3>Choose Your Vibe! ✨</h3>
                                    <button
                                        onClick={() => setShowVibeSelector(false)}
                                        className="close-vibe-btn"
                                    >
                                        ×
                                    </button>
                                </div>
                                <div className="vibe-grid">
                                    {[
                                        { id: 'cute', name: 'Cute Vibes', emoji: '✨💕', desc: 'Super kawaii and adorable!' },
                                        { id: 'happy', name: 'Happy Vibes', emoji: '🎉🌟', desc: 'Energetic and joyful!' },
                                        { id: 'doggie', name: 'Doggie Vibes', emoji: '🐕🐾', desc: 'Woof woof! So excited!' },
                                        { id: 'memes', name: 'Meme Vibes', emoji: '😂🔥', desc: 'Based and bussin!' },
                                        { id: 'mysterious', name: 'Mysterious Vibes', emoji: '✨🔮', desc: 'Enigmatic and mystical!' },
                                        { id: 'chill', name: 'Chill Vibes', emoji: '😎🌊', desc: 'Laid back and relaxed!' },
                                        { id: 'sassy', name: 'Sassy Vibes', emoji: '💅✨', desc: 'Witty with attitude!' },
                                        { id: 'wholesome', name: 'Wholesome Vibes', emoji: '💖🤗', desc: 'Caring and supportive!' }
                                    ].map((vibe) => (
                                        <button
                                            key={vibe.id}
                                            className={`vibe-option ${selectedVibe === vibe.id ? 'active' : ''}`}
                                            onClick={() => {
                                                setSelectedVibe(vibe.id);
                                                setShowVibeSelector(false);
                                            }}
                                        >
                                            <div className="vibe-emoji">{vibe.emoji}</div>
                                            <div className="vibe-name">{vibe.name}</div>
                                            <div className="vibe-desc">{vibe.desc}</div>
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* User List Sidebar */}
                        {showUserList && (
                            <div
                                className={`user-list-sidebar ${isDraggingUserList ? 'dragging' : ''} ${isMobile ? 'mobile-fullscreen-window' : ''}`}
                                ref={userListRef}
                                style={isMobile ? {
                                    position: 'fixed',
                                    top: 0,
                                    left: 0,
                                    width: '100vw',
                                    height: '100vh',
                                    transform: 'none',
                                    zIndex: 10002
                                } : {
                                    position: 'fixed',
                                    left: userListPosition.x,
                                    top: userListPosition.y,
                                    transform: isDraggingUserList ? 'scale(1.02)' : 'scale(1)',
                                    transition: isDraggingUserList ? 'none' : 'transform 0.1s ease'
                                }}
                            >
                                <div
                                    className="user-list-header"
                                    onMouseDown={handleUserListMouseDown}
                                    style={{ cursor: isDraggingUserList ? 'grabbing' : 'grab' }}
                                >
                                    <h3>All Users</h3>
                                    <button
                                        onClick={() => setShowUserList(false)}
                                        className="close-user-list-btn"
                                    >
                                        ×
                                    </button>
                                </div>
                                <div className="user-list-content">
                                    {allUsers.length === 0 ? (
                                        <div className="no-users">
                                            <p>No users found</p>
                                        </div>
                                    ) : (
                                        <div className="users-list">
                                            {console.log('Rendering users, unread users:', Array.from(usersWithUnreadMessages))}
                                            {allUsers.map((userItem) => (
                                                <div
                                                    key={userItem.id}
                                                    className={`user-item ${userItem.id === user.id ? 'current-user' : ''} ${targetHandle === userItem.handle ? 'selected' : ''} ${usersWithUnreadMessages.has(userItem.id) ? 'has-unread' : ''}`}
                                                    onClick={() => {
                                                        if (userItem.id === user.id) {
                                                            setShowProfileModal(true);
                                                        } else {
                                                            setTargetHandle(userItem.handle);
                                                            // Close user list on mobile when user is selected
                                                            if (isMobile) {
                                                                setShowUserList(false);
                                                            }
                                                        }
                                                    }}
                                                >
                                                    <div className={`user-avatar ${blinkingUsers.has(userItem.id) ? 'blinking' : ''}`}>
                                                        {userItem.profilePicture === 'cat1' && '🐱'}
                                                        {userItem.profilePicture === 'cat2' && '😸'}
                                                        {userItem.profilePicture === 'cat3' && '😺'}
                                                        {userItem.profilePicture === 'default' && '👤'}
                                                        {usersWithUnreadMessages.has(userItem.id) && (
                                                            <div className="notification-dot" title={`Unread messages from ${userItem.name}`}></div>
                                                        )}
                                                    </div>
                                                    <div className="user-info">
                                                        <div className="user-name">{userItem.name}</div>
                                                        <div className="user-handle">@{userItem.handle}</div>
                                                        <div className={`user-status ${userItem.isOnline ? (userItem.status || 'online') : 'offline'}`}>
                                                            {userItem.isOnline ? `● ${userItem.status || 'Online'}` : '● Offline'}
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* Profile Modal */}
                        {showProfileModal && (
                            <div className="profile-modal">
                                <div className="profile-modal-content">
                                    <div className="profile-modal-header">
                                        <h3>Your Profile</h3>
                                        <button
                                            onClick={() => setShowProfileModal(false)}
                                            className="close-profile-btn"
                                        >
                                            ×
                                        </button>
                                    </div>
                                    <div className="profile-modal-body">
                                        <div className="profile-picture-section">
                                            <h4>Profile Picture</h4>
                                            <div className="profile-picture-options">
                                                <button
                                                    className={`profile-pic-option ${userProfilePicture === 'default' ? 'selected' : ''}`}
                                                    onClick={() => setUserProfilePicture('default')}
                                                >
                                                    👤
                                                </button>
                                                <button
                                                    className={`profile-pic-option ${userProfilePicture === 'cat1' ? 'selected' : ''}`}
                                                    onClick={() => setUserProfilePicture('cat1')}
                                                >
                                                    🐱
                                                </button>
                                                <button
                                                    className={`profile-pic-option ${userProfilePicture === 'cat2' ? 'selected' : ''}`}
                                                    onClick={() => setUserProfilePicture('cat2')}
                                                >
                                                    😸
                                                </button>
                                                <button
                                                    className={`profile-pic-option ${userProfilePicture === 'cat3' ? 'selected' : ''}`}
                                                    onClick={() => setUserProfilePicture('cat3')}
                                                >
                                                    😺
                                                </button>
                                            </div>
                                        </div>
                                        <div className="status-section">
                                            <h4>Status</h4>
                                            <div className="status-options">
                                                <button
                                                    className={`status-option ${userStatus === 'online' ? 'selected' : ''}`}
                                                    onClick={() => setUserStatus('online')}
                                                >
                                                    ● Online
                                                </button>
                                                <button
                                                    className={`status-option ${userStatus === 'busy' ? 'selected' : ''}`}
                                                    onClick={() => setUserStatus('busy')}
                                                >
                                                    ● Busy
                                                </button>
                                                <button
                                                    className={`status-option ${userStatus === 'hidden' ? 'selected' : ''}`}
                                                    onClick={() => setUserStatus('hidden')}
                                                >
                                                    ● Hidden
                                                </button>
                                            </div>
                                        </div>
                                        <div className="profile-actions">
                                            <button
                                                className="win98-button"
                                                onClick={async () => {
                                                    try {
                                                        // Update user profile in Firebase
                                                        await updateUserStatus(user.id, true, userProfilePicture, userStatus);
                                                        setShowProfileModal(false);
                                                        // Refresh user list to reflect status changes
                                                        await refreshUserList();
                                                    } catch (error) {
                                                        console.error('Error updating profile:', error);
                                                        alert('Error updating profile. Please try again.');
                                                    }
                                                }}
                                            >
                                                Save Changes
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        <div className="chat-container">
                            {/* Chat History Sidebar */}
                            {showSidebar && (
                                <div className="chat-sidebar">
                                    <div className="sidebar-header">
                                        <h3>Chat History</h3>
                                        <button
                                            onClick={() => setShowSidebar(false)}
                                            className="close-sidebar-btn"
                                        >
                                            ×
                                        </button>
                                    </div>
                                    <div className="sidebar-content">
                                        {chatHistory.length === 0 ? (
                                            <div className="no-chats">
                                                <p>No previous chats</p>
                                                <small>Start a conversation to see it here</small>
                                            </div>
                                        ) : (
                                            <div className="chat-list">
                                                {chatHistory.map((chat, index) => (
                                                    <div
                                                        key={index}
                                                        className={`chat-item ${targetHandle === chat.handle ? 'active' : ''}`}
                                                        onClick={() => switchToChat(chat.handle)}
                                                    >
                                                        <div className="chat-avatar">👤</div>
                                                        <div className="chat-info">
                                                            <div className="chat-name">{chat.name}</div>
                                                            <div className="chat-handle">@{chat.handle}</div>
                                                            {chat.lastMessage && (
                                                                <div className="chat-preview">{chat.lastMessage}</div>
                                                            )}
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}

                            <div className="chat-main">
                                <MessageList
                                    messages={messages}
                                    currentUserId={user.id}
                                    isTyping={isTyping}
                                />
                                <div ref={messagesEndRef} />
                            </div>
                        </div>

                        <MessageInput
                            onSendMessage={handleSendMessage}
                            disabled={isTransforming}
                            messages={messages}
                            currentUserId={user.id}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ChatRoom;
